import React, { useState, useEffect } from 'react';
import { sheppardProfile as mockData } from './mockData/showcaseMockData';
import HeroSection from './sections/HeroSection';
import AssessmentDemo from './sections/AssessmentDemo';
import ProfileDemo from './sections/ProfileDemo';
import TimelineDemo from './sections/TimelineDemo';
import CollegeSelectionDemo from './sections/CollegeSelectionDemo';
import EssayDemo from './sections/EssayDemo';
import RecommendationDemo from './sections/RecommendationDemo';
import InsightsDemo from './sections/InsightsDemo';
import ResultsSection from './sections/ResultsSection';
import {
  Sparkles,
  Brain,
  Calendar,
  Award,
  Target,
  FileText,
  Users,
  Zap,
  TrendingUp,
  ChevronDown
} from 'lucide-react';

const CollegeJourneyShowcaseAspirational = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  const sections = [
    { id: 'hero', name: 'Start', icon: Sparkles },
    { id: 'assessment', name: 'Assessment', icon: Brain },
    { id: 'profile', name: 'Profile', icon: Award },
    { id: 'timeline', name: 'Timeline', icon: Calendar },
    { id: 'colleges', name: 'Colleges', icon: Target },
    { id: 'essays', name: 'Essays', icon: FileText },
    { id: 'recommendations', name: 'Letters', icon: Users },
    { id: 'insights', name: 'Insights', icon: Zap },
    { id: 'results', name: 'Results', icon: TrendingUp }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      const sectionElements = sections.map(s => document.getElementById(s.id));

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const element = sectionElements[i];
        if (element && element.offsetTop <= scrollPosition) {
          setCurrentSection(i);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (index) => {
    const element = document.getElementById(sections[index].id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleStart = () => {
    setHasStarted(true);
    scrollToSection(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation Dots */}
      {hasStarted && (
        <div className="fixed right-8 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col gap-4">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(index)}
                className={`group relative flex items-center justify-center w-10 h-10 rounded-full transition-all
                  ${currentSection === index
                    ? 'bg-azure-600 shadow-lg scale-110'
                    : 'bg-white shadow-md hover:shadow-lg hover:scale-105'}`}
              >
                <Icon className={`w-5 h-5 ${currentSection === index ? 'text-white' : 'text-slate-600'}`} />

                {/* Tooltip */}
                <div className="absolute right-14 px-2 py-1 bg-slate-900 text-white text-xs rounded
                  opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  {section.name}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Sections */}
      <section id="hero" className="min-h-screen flex items-center justify-center py-20">
        <HeroSection onStart={handleStart} />
      </section>

      <section id="assessment" className="min-h-screen flex items-center justify-center py-20">
        <AssessmentDemo data={mockData} />
      </section>

      <section id="profile" className="min-h-screen flex items-center justify-center py-20">
        <ProfileDemo data={mockData} />
      </section>

      <section id="timeline" className="min-h-screen flex items-center justify-center py-20">
        <TimelineDemo data={mockData} />
      </section>

      <section id="colleges" className="min-h-screen flex items-center justify-center py-20">
        <CollegeSelectionDemo data={mockData} />
      </section>

      <section id="essays" className="min-h-screen flex items-center justify-center py-20">
        <EssayDemo data={mockData} />
      </section>

      <section id="recommendations" className="min-h-screen flex items-center justify-center py-20">
        <RecommendationDemo data={mockData} />
      </section>

      <section id="insights" className="min-h-screen flex items-center justify-center py-20">
        <InsightsDemo data={mockData} />
      </section>

      <section id="results" className="min-h-screen flex items-center justify-center py-20 pb-40">
        <ResultsSection data={mockData} />
      </section>

      {/* Scroll Indicator */}
      {!hasStarted && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-slate-400" />
        </div>
      )}

      {/* Aspirational Version Notice */}
      <div className="fixed bottom-4 left-4 z-50 bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm font-medium">
        Aspirational Demo (Future Vision)
      </div>
    </div>
  );
};

export default CollegeJourneyShowcaseAspirational;